/**
 * Draft state + persistence for the S4 Industry Pack editor (WF-F2 / DR2).
 *
 * Sectioned thick authoring for identity + append I/O/context/instructions +
 * resource_types; legacy seed JSON panels remain in advanced collapse.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getIndustryPack,
  publishIndustryPack,
  updateIndustryPack,
} from '@/services/aiStudioAgentService';
import type {
  AiStudioIndustryPack,
  ExpectedFieldRow,
  PackAuthoringPatch,
  ResourceTypeSeed,
  UpdatePackRequest,
} from '@/types/aiStudioAgent';

export type PackJsonField =
  | 'vocabularyMap'
  | 'enabledPlugins'
  | 'defaultWeights'
  | 'importTemplate';

interface PackDraft {
  name: string;
  domain: string;
  description: string;
  purpose: string;
  scopeHonesty: string;
  expertPosture: string;
  appendInputExpected: ExpectedFieldRow[];
  appendOutputExpected: ExpectedFieldRow[];
  appendContext: string;
  appendInstructions: string;
  resourceTypes: ResourceTypeSeed[];
  vocabularyMap: Record<string, unknown>;
  enabledPlugins: unknown[];
  defaultWeights: Record<string, unknown>;
  importTemplate: Record<string, unknown>;
}

function toDraft(pack: AiStudioIndustryPack): PackDraft {
  return {
    name: pack.name,
    domain: pack.domain ?? '',
    description: pack.description ?? '',
    purpose: pack.purpose ?? '',
    scopeHonesty: pack.scopeHonesty ?? '',
    expertPosture: pack.expertPosture ?? '',
    appendInputExpected: pack.appendInputExpected,
    appendOutputExpected: pack.appendOutputExpected,
    appendContext: pack.appendContext ?? '',
    appendInstructions: pack.appendInstructions ?? '',
    resourceTypes: pack.resourceTypes,
    vocabularyMap: pack.vocabularyMap,
    enabledPlugins: pack.enabledPlugins,
    defaultWeights: pack.defaultWeights,
    importTemplate: pack.importTemplate,
  };
}

function toRequest(draft: PackDraft): UpdatePackRequest {
  return {
    name: draft.name.trim(),
    domain: draft.domain.trim() || null,
    description: draft.description.trim() || null,
    purpose: draft.purpose.trim() || null,
    scopeHonesty: draft.scopeHonesty.trim() || null,
    expertPosture: draft.expertPosture.trim() || null,
    appendInputExpected: draft.appendInputExpected,
    appendOutputExpected: draft.appendOutputExpected,
    appendContext: draft.appendContext.trim() || null,
    appendInstructions: draft.appendInstructions.trim() || null,
    resourceTypes: draft.resourceTypes,
    vocabularyMap: draft.vocabularyMap,
    enabledPlugins: draft.enabledPlugins,
    defaultWeights: draft.defaultWeights,
    importTemplate: draft.importTemplate,
  };
}

export interface UsePackEditorResult {
  pack: AiStudioIndustryPack | null;
  draft: PackDraft | null;
  loading: boolean;
  saving: boolean;
  publishing: boolean;
  dirty: boolean;
  jsonValid: boolean;
  readOnly: boolean;
  showAdvancedJson: boolean;
  errorKey: string | null;
  setField: <K extends keyof PackDraft>(field: K, value: PackDraft[K]) => void;
  setJsonValidity: (field: PackJsonField, valid: boolean) => void;
  setShowAdvancedJson: (open: boolean) => void;
  save: () => Promise<void>;
  publish: () => Promise<void>;
  applyAuthoringPatch: (patch: PackAuthoringPatch) => void;
}

export function usePackEditor(packId: string | undefined): UsePackEditorResult {
  const [pack, setPack] = useState<AiStudioIndustryPack | null>(null);
  const [draft, setDraft] = useState<PackDraft | null>(null);
  const [baseline, setBaseline] = useState<PackDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showAdvancedJson, setShowAdvancedJson] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [invalidJsonFields, setInvalidJsonFields] = useState<string[]>([]);

  useEffect(() => {
    if (!packId) {
      setLoading(false);
      setErrorKey('aiStudio.packs.notFound');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    setInvalidJsonFields([]);

    getIndustryPack(packId)
      .then((loaded) => {
        if (cancelled) return;
        setPack(loaded);
        const next = toDraft(loaded);
        setDraft(next);
        setBaseline(next);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.packs.loadEditorFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [packId]);

  const dirty = useMemo(() => {
    if (!draft || !baseline) return false;
    return JSON.stringify(draft) !== JSON.stringify(baseline);
  }, [baseline, draft]);

  const readOnly = pack?.lifecycleStatus !== 'draft';

  const setField = useCallback(<K extends keyof PackDraft>(field: K, value: PackDraft[K]) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  }, []);

  const setJsonValidity = useCallback((field: PackJsonField, valid: boolean) => {
    setInvalidJsonFields((current) => {
      if (valid) return current.filter((item) => item !== field);
      return current.includes(field) ? current : [...current, field];
    });
  }, []);

  const save = useCallback(async () => {
    if (!packId || !draft || readOnly) return;
    setSaving(true);
    setErrorKey(null);
    try {
      const updated = await updateIndustryPack(packId, toRequest(draft));
      setPack(updated);
      const next = toDraft(updated);
      setDraft(next);
      setBaseline(next);
      setInvalidJsonFields([]);
    } catch {
      setErrorKey('aiStudio.packs.saveFailed');
    } finally {
      setSaving(false);
    }
  }, [draft, packId, readOnly]);

  const publish = useCallback(async () => {
    if (!packId || !draft || readOnly) return;
    setPublishing(true);
    setErrorKey(null);
    try {
      if (dirty) {
        const updated = await updateIndustryPack(packId, toRequest(draft));
        setPack(updated);
        const next = toDraft(updated);
        setDraft(next);
        setBaseline(next);
      }
      const published = await publishIndustryPack(packId);
      setPack(published);
      const frozen = toDraft(published);
      setDraft(frozen);
      setBaseline(frozen);
      setInvalidJsonFields([]);
    } catch {
      setErrorKey('aiStudio.packs.publishFailed');
    } finally {
      setPublishing(false);
    }
  }, [dirty, draft, packId, readOnly]);

  const applyAuthoringPatch = useCallback((patch: PackAuthoringPatch) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.domain !== undefined ? { domain: patch.domain } : {}),
        ...(patch.description !== undefined ? { description: patch.description } : {}),
        ...(patch.purpose !== undefined ? { purpose: patch.purpose } : {}),
        ...(patch.scopeHonesty !== undefined ? { scopeHonesty: patch.scopeHonesty } : {}),
        ...(patch.expertPosture !== undefined ? { expertPosture: patch.expertPosture } : {}),
        ...(patch.appendContext !== undefined ? { appendContext: patch.appendContext } : {}),
        ...(patch.appendInstructions !== undefined
          ? { appendInstructions: patch.appendInstructions }
          : {}),
        ...(patch.appendInputExpected !== undefined
          ? { appendInputExpected: patch.appendInputExpected as ExpectedFieldRow[] }
          : {}),
        ...(patch.appendOutputExpected !== undefined
          ? { appendOutputExpected: patch.appendOutputExpected as ExpectedFieldRow[] }
          : {}),
        ...(patch.resourceTypes !== undefined
          ? { resourceTypes: patch.resourceTypes as ResourceTypeSeed[] }
          : {}),
      };
    });
  }, []);

  return {
    pack,
    draft,
    loading,
    saving,
    publishing,
    dirty,
    jsonValid: invalidJsonFields.length === 0,
    readOnly,
    showAdvancedJson,
    errorKey,
    setField,
    setJsonValidity,
    setShowAdvancedJson,
    save,
    publish,
    applyAuthoringPatch,
  };
}
